// src/components/docs/MDXProvider.tsx — Provides custom components to MDX content
//
// Replaces Next.js mdx-components.tsx. Wraps MDX content with @mdx-js/react
// MDXProvider so custom components (LanguageBlock, Block, etc.) are available
// without explicit imports in .mdx files.

import type { ReactNode, ComponentType } from "react";
import { MDXProvider as BaseMDXProvider } from "@mdx-js/react";
import CodeTabs from "@/components/docs/CodeTabs";
import ApiExample from "@/components/docs/ApiExample";
import Callout from "@/components/docs/Callout";
import LanguageBlock, { Block } from "@/components/docs/LanguageBlock";
import DocsNextStep from "@/components/docs/DocsNextStep";

function slugify(children: ReactNode): string {
  const text = extractText(children);
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

function extractText(node: ReactNode): string {
  if (typeof node === "string") return node;
  if (typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(extractText).join("");
  if (node && typeof node === "object" && "props" in node) {
    return extractText((node as { props: { children?: ReactNode } }).props.children);
  }
  return "";
}

const components: Record<string, ComponentType<any>> = {
  // Custom documentation components
  CodeTabs,
  ApiExample,
  Callout,
  LanguageBlock,
  Block,
  DocsNextStep,

  // Typography
  h1: ({ children }: { children: ReactNode }) => (
    <h1 id={slugify(children)} className="docs-h1">{children}</h1>
  ),
  h2: ({ children }: { children: ReactNode }) => (
    <h2 id={slugify(children)} className="docs-h2">{children}</h2>
  ),
  h3: ({ children }: { children: ReactNode }) => (
    <h3 id={slugify(children)} className="docs-h3">{children}</h3>
  ),
  h4: ({ children }: { children: ReactNode }) => (
    <h4 id={slugify(children)} className="docs-h4">{children}</h4>
  ),
  p: ({ children }: { children: ReactNode }) => (
    <p className="docs-p">{children}</p>
  ),
  ul: ({ children }: { children: ReactNode }) => (
    <ul className="docs-ul">{children}</ul>
  ),
  ol: ({ children }: { children: ReactNode }) => (
    <ol className="docs-ol">{children}</ol>
  ),
  li: ({ children }: { children: ReactNode }) => (
    <li className="docs-li">{children}</li>
  ),
  a: ({ href, children }: { href?: string; children: ReactNode }) => (
    <a href={href} className="docs-a">{children}</a>
  ),
  code: ({ children }: { children: ReactNode }) => (
    <code className="docs-code-inline">{children}</code>
  ),
  pre: ({ children }: { children: ReactNode }) => (
    <div className="docs-pre-wrapper">
      <pre className="docs-pre">{children}</pre>
    </div>
  ),
  blockquote: ({ children }: { children: ReactNode }) => (
    <blockquote className="docs-blockquote">{children}</blockquote>
  ),
  table: ({ children }: { children: ReactNode }) => (
    <div className="docs-table-container">
      <table className="docs-table">{children}</table>
    </div>
  ),
  thead: ({ children }: { children: ReactNode }) => (
    <thead className="docs-thead">{children}</thead>
  ),
  th: ({ children }: { children: ReactNode }) => (
    <th className="docs-th">{children}</th>
  ),
  td: ({ children }: { children: ReactNode }) => (
    <td className="docs-td">{children}</td>
  ),
  hr: () => <hr className="docs-hr" />,
  strong: ({ children }: { children: ReactNode }) => <strong className="font-semibold">{children}</strong>,
  em: ({ children }: { children: ReactNode }) => <em>{children}</em>,
};

export default function DocsMDXProvider({ children }: { children: ReactNode }) {
  return <BaseMDXProvider components={components}>{children}</BaseMDXProvider>;
}
